import { catalogServiceClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { ApiError } from '~/api/errors';

import { toFeedProductRow, toItemsPage } from '../mappers/curation.mapper';
import type { CatalogEnvelope } from '../types/catalog-service.api.types';
import type {
  AlreadyCuratedRowsPage,
  BulkUpdateNeedsAttentionData,
  BySelectionRowsRequest,
  CurationFilterOption,
  CurationRunOutcomes,
  CurationRunProductsPage,
  CurationRunProductsQueryRequest,
  CurationRunStats,
  RunAlreadyCuratedPage,
  RunAlreadyCuratedQueryRequest,
  SkippedProductsPage,
  UpdateNeedsAttentionRequest,
} from '../types/curation.api.types';
import type {
  AlreadyCuratedRowsResult,
  RunAlreadyCuratedResult,
  RunProductsResult,
  SkippedProductsResult,
} from '../types/curation.types';
import { toSearchParams, unwrap } from '../utils/envelope';

import { noteFlagWrite } from './needs-attention.service';

/** Query params of GET curation-runs/{runId}/products. */
export interface RunProductsParams {
  search?: string;
  /** `true` flagged only, `false` clean only, omitted for all. */
  needsAttention?: boolean;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page: number;
  size: number;
  /**
   * `summary` = list-row keys only (no description / details / attributes / raw), `full` the
   * whole item (§50; an older backend ignores it and always sends `full`).
   */
  fields?: RunProductFields;
}

/** Params of POST curation-runs/{runId}/products/query — the GET's id-set twin. */
export interface RunProductsLookupParams {
  /** Feed-product ids to look up (max 1000); ids outside the run are simply omitted. */
  productIds: string[];
  page: number;
  size: number;
  /** As on `RunProductsParams`; defaults to `full`. */
  fields?: RunProductFields;
}

/** How much of each run-products item to return (api-changes §50). */
export type RunProductFields = CurationRunProductsQueryRequest['fields'];

/** Which rows count as the run's (§50): `auto` (default) = still held ∪ the ledger's; `current` = held now. */
export type RunMembershipSource = CurationRunProductsQueryRequest['source'];

/**
 * True for §50's 422 `details.code = LEDGER_TOO_LARGE`: an old run's ledger names more listings
 * (> 200,000) than one `source=auto` read resolves — the read must use `source=current`.
 */
export function isLedgerTooLarge(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.statusCode !== 422) return false;
  const details = (error.body as { details?: { code?: unknown } } | null)?.details;
  return details?.code === 'LEDGER_TOO_LARGE';
}

/** Runs the backend refused to resolve from the ledger — read with `source=current` from then on. */
const currentOnlyRuns = new Set<string>();

/**
 * Reads with the default `source=auto`; on `LEDGER_TOO_LARGE` re-reads with `source=current` (the
 * rows the run still holds) and remembers the run for the session, so later pages skip the
 * refused read. `currentOnly` tells the screen to say so.
 */
export async function readRunMembers<T>(
  runId: string,
  read: (source: RunMembershipSource | undefined) => Promise<T>,
): Promise<{ data: T; currentOnly: boolean }> {
  if (!currentOnlyRuns.has(runId)) {
    try {
      return { data: await read(undefined), currentOnly: false };
    } catch (error) {
      if (!isLedgerTooLarge(error)) throw error;
      currentOnlyRuns.add(runId);
    }
  }
  return { data: await read('current'), currentOnly: true };
}

/** Total / valid / flagged and the curation funnel for one run (the curation run id IS the run id). */
export async function getCurationRunStats(runId: string): Promise<CurationRunStats> {
  // An old run past the ledger cap reads its stats over the rows it still holds (see readRunMembers).
  const { data } = await readRunMembers(runId, async (source) => {
    const body = await catalogServiceClient
      .get(endpoints.catalogService.curation.runStats(runId), {
        searchParams: toSearchParams({ source }),
      })
      .json<CatalogEnvelope<CurationRunStats>>();
    return unwrap(body);
  });
  return data;
}

/** What the run's CURATION stage did — new / matched / variants / first curated / flagged / … counts. */
export async function getCurationRunOutcomes(runId: string): Promise<CurationRunOutcomes> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.curation.runOutcomes(runId))
    .json<CatalogEnvelope<CurationRunOutcomes>>();
  return unwrap(body);
}

/** One page of a run's feed products, with run-wide counts that ignore the flag filter. */
export async function listCurationRunProducts(
  runId: string,
  params: RunProductsParams,
): Promise<RunProductsResult> {
  const { data, currentOnly } = await readRunMembers(runId, async (source) => {
    const body = await catalogServiceClient
      .get(endpoints.catalogService.curation.runProducts(runId), {
        searchParams: toSearchParams({ ...params, source }),
      })
      .json<CatalogEnvelope<CurationRunProductsPage>>();
    return unwrap(body);
  });
  return {
    page: toItemsPage(data.pagination, data.items),
    counts: data.counts,
    connectorCode: data.meta.connectorCode,
    status: data.meta.status ?? null,
    currentOnly,
  };
}

/** Rows a `recordSkipped` run passed over as already curated (empty for any other run). */
export async function listCurationSkippedProducts(
  runId: string,
  params: { page: number; size: number },
): Promise<SkippedProductsResult> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.curation.runSkippedProducts(runId), {
      searchParams: toSearchParams(params),
    })
    .json<CatalogEnvelope<SkippedProductsPage>>();
  const data = unwrap(body);
  return {
    page: toItemsPage(data.pagination, data.items),
    counts: data.counts,
  };
}

/** Filter definitions for a connector; omit the connector for the overlap set every connector shares. */
export async function getCurationFilters(
  connectorCode: string | null,
): Promise<CurationFilterOption[]> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.curation.filters(), {
      searchParams: toSearchParams({ connectorCode }),
    })
    .json<CatalogEnvelope<CurationFilterOption[] | null>>();
  return body.data ?? [];
}

/**
 * The same page as `listCurationRunProducts`, filtered to an explicit feed-product id
 * set (up to 1000) instead of the GET's filters — "look up by id" on the run-products
 * table. Ids outside this run are simply omitted from the result.
 */
export async function queryCurationRunProducts(
  runId: string,
  params: RunProductsLookupParams,
): Promise<RunProductsResult> {
  // `source` / `fields` are §50 params; an older backend ignores unknown body keys.
  // `auto` (its default) lists an old run's rows a later run took over, flagged `still_current`.
  const { data, currentOnly } = await readRunMembers(runId, async (source) => {
    const request: CurationRunProductsQueryRequest = {
      source: source ?? 'auto',
      fields: 'full',
      ...params,
    };
    const body = await catalogServiceClient
      .post(endpoints.catalogService.curation.runProductsQuery(runId), { json: request })
      .json<CatalogEnvelope<CurationRunProductsPage>>();
    return unwrap(body);
  });
  return {
    page: toItemsPage(data.pagination, data.items),
    counts: data.counts,
    connectorCode: data.meta.connectorCode,
    status: data.meta.status ?? null,
    currentOnly,
  };
}

/**
 * Which of a selection (or, when omitted, the run's own trigger scope) this run
 * skipped as already curated — the durable per-run partition, immune to later
 * catalog churn (unlike a point-in-time catalog snapshot).
 */
export async function getRunAlreadyCurated(
  runId: string,
  request: RunAlreadyCuratedQueryRequest,
): Promise<RunAlreadyCuratedResult> {
  const body = await catalogServiceClient
    .post(endpoints.catalogService.curation.runAlreadyCurated(runId), { json: request })
    .json<CatalogEnvelope<RunAlreadyCuratedPage>>();
  const data = unwrap(body);
  return {
    page: toItemsPage(data.pagination, data.items),
    counts: data.counts,
    meta: data.meta,
  };
}

/**
 * Keyset page of the rows behind one already-curated bucket (`clean` or `flagged`)
 * for a SUBSET selection of source product ids. Items arrive as the same
 * snake_case shared-serializer bag every curation listing uses, read defensively.
 */
export async function getAlreadyCuratedRows(
  request: BySelectionRowsRequest,
): Promise<AlreadyCuratedRowsResult> {
  const body = await catalogServiceClient
    .post(endpoints.catalogService.curation.alreadyCuratedRows(), { json: request })
    .json<CatalogEnvelope<AlreadyCuratedRowsPage>>();
  const data = unwrap(body);
  return {
    items: data.items.map(toFeedProductRow),
    nextCursor: data.nextCursor ?? null,
    meta: data.meta,
  };
}

/** Flags or clears products inside a run, at both the run and the durable product level. */
export async function updateRunNeedsAttention(
  runId: string,
  request: UpdateNeedsAttentionRequest,
): Promise<BulkUpdateNeedsAttentionData> {
  const body = await catalogServiceClient
    .post(endpoints.catalogService.curation.runNeedsAttention(runId), { json: request })
    .json<CatalogEnvelope<BulkUpdateNeedsAttentionData>>();
  noteFlagWrite();
  return unwrap(body);
}
